import {
  IonButton,
  IonButtons,
  IonContent,
  IonHeader,
  IonIcon,
  IonInput,
  IonItem,
  IonLabel,
  IonList,
  IonLoading,
  IonModal,
  IonSelect,
  IonSelectOption,
  IonTitle,
  IonToolbar,
} from "@ionic/react";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { caretDownOutline, caretDownSharp } from "ionicons/icons";
import { useParams } from "react-router-dom";
import { getCCACascadeValues, getCCARequiredFlags } from "../api/AnalysisCodes";
import {
  AnalysisCode,
  CascadeValue,
  CompletionFieldDef,
  ValidValue,
} from "../models/analysiscodes/AnalysisCodes";
import { decodeParam } from "../util/ApiHelper";
import { compareValidValuesByDisplayText } from "../util/DropdownDisplayModeHelper";
import "./AnalysisCodes.css";
import { TranslationsContext } from "../util/Translations";
import { useWorkOrderStore } from "../store/wo-order";
import { useShallow } from "zustand/react/shallow";
import { isFieldRequired } from "../util/MetadataHelper";

/* Bug 873321 - Component / Condition / Action accept typed text that filters the list, instead of
   only being scrollable. Nothing else about the fields changes: the same values are offered, in
   the same order, showing the same text, and selecting one does exactly what picking it from the
   dropdown did. With nothing typed the list is the full set, as before. */
interface CodeFieldProps {
  field?: CompletionFieldDef;
  fallbackLabel: string;
  isRequired: boolean;
  values: ValidValue[];
  value?: string;
  disabled: boolean;
  isOpen: boolean;
  onOpenChange: (isOpen: boolean) => void;
  onSelect: (value?: string) => void;
}

const CodeField: React.FC<CodeFieldProps> = (props) => {
  const {
    field,
    fallbackLabel,
    isRequired,
    values,
    value,
    disabled,
    isOpen,
    onOpenChange,
    onSelect,
  } = props;
  const [filter, setFilter] = useState("");

  // The same text the dropdown option carried.
  const optionText = (validValue?: ValidValue) =>
    !validValue
      ? ""
      : `${validValue.Code}${
          validValue.Description ? ` - ${validValue.Description}` : ""
        }`;

  /* A code cleared by the cascade effects must not leave the text typed for the previous
     selection sitting in the field. */
  useEffect(() => {
    if (!value) setFilter("");
  }, [value]);

  /* Closed from outside - the modal was dismissed, or another field was opened - drops the
     filter as well, so reopening never starts on text typed for a previous selection. */
  useEffect(() => {
    if (!isOpen) setFilter("");
  }, [isOpen]);

  const selected = values.find((validValue) => validValue.Code === value);
  const search = filter.trim().toLowerCase();
  /* Filters the list the cascade effects already narrowed, so typing can never offer a code the
     mapping rules exclude. */
  const shown = search
    ? values.filter(
        (validValue) =>
          (validValue.Code || "").toLowerCase().includes(search) ||
          (validValue.Description || "").toLowerCase().includes(search)
      )
    : values;

  const close = () => {
    setFilter("");
    onOpenChange(false);
  };

  return (
    <React.Fragment>
      <IonItem>
        <IonLabel position="stacked" color={isRequired ? "danger" : undefined}>
          {field?.TranslatedIdText || fallbackLabel}
          {isRequired ? " *" : ""}
        </IonLabel>
        <IonInput
          value={isOpen ? filter : optionText(selected) || value || ""}
          disabled={disabled}
          onIonFocus={() => onOpenChange(true)}
          onIonInput={(ev) => {
            const typed = (ev.target as HTMLIonInputElement)?.value ?? "";
            setFilter(String(typed));
            onOpenChange(true);
          }}
        />
        <IonIcon
          slot="end"
          ios={caretDownOutline}
          md={caretDownSharp}
          /* Anchored to the input line rather than centred in the item: the item grows and
             shrinks as the label wraps and a value is chosen, and a centred caret moved with it,
             landing somewhere different each time it was reached for. The padding is hit area -
             the glyph is small enough to be awkward to tap accurately without it. */
          style={{
            fontSize: "16px",
            alignSelf: "flex-end",
            marginBottom: "6px",
            padding: "8px",
            cursor: "pointer",
          }}
          onClick={() => {
            if (disabled) return;
            if (isOpen) close();
            else onOpenChange(true);
          }}
        />
      </IonItem>
      {isOpen && !disabled && (
        <IonList lines="full">
          {shown.map((validValue, index) => (
            <IonItem
              button
              key={`${validValue.Code}-${index}`}
              onClick={() => {
                onSelect(validValue.Code);
                close();
              }}
            >
              <IonLabel>{optionText(validValue)}</IonLabel>
            </IonItem>
          ))}
        </IonList>
      )}
    </React.Fragment>
  );
};

interface AnalysisCodesModalProps {
  isOpen: boolean;
  analysisCodeForEdit?: AnalysisCode;
  analysisCodes: AnalysisCode[];
  onCancel: () => void;
  onSave: (analysisCode?: AnalysisCode) => void;
}

const AnalysisCodesModal: React.FC<AnalysisCodesModalProps> = (props) => {
  const { isOpen, analysisCodeForEdit, analysisCodes, onCancel, onSave } =
    props;
  const { translations } = useContext(TranslationsContext);
  const { workOrderId } = useParams<{ workOrderId: string }>();
  const displayWo = decodeParam(workOrderId);
  const [analysisCode, setAnalysisCode] = useState<AnalysisCode>();
  const [componentField, setComponentField] = useState<CompletionFieldDef>();
  const [conditionField, setConditionField] = useState<CompletionFieldDef>();
  const [actionField, setActionField] = useState<CompletionFieldDef>();
  const [showLoading, setShowLoading] = useState(false);
  /* Which field's list is showing, held here so opening one closes the others. */
  const [openField, setOpenField] = useState<string>();
  const [componentValues, setComponentValues] = useState<ValidValue[]>([]);
  const [conditionValues, setConditionValues] = useState<ValidValue[]>([]);
  const [actionValues, setActionValues] = useState<ValidValue[]>([]);
  const [_, setComponentCascadeValues] = useState<CascadeValue[]>([]);
  const [conditionCascadeValues, setConditionCascadeValues] = useState<
    CascadeValue[]
  >([]);
  const [actionCascadeValues, setActionCascadeValues] = useState<
    CascadeValue[]
  >([]);
  /* US 906024 - the cascade "required" rules that apply to this Component / Condition / Action
     chain for the codes currently picked. Re-evaluated on every selection, because the rule on
     Action is qualified by the Component chosen above it. */
  const [ccaRequired, setCcaRequired] = useState({
    componentRequired: false,
    conditionRequired: false,
    actionRequired: false,
  });

  const ccaFieldDefinitions = useWorkOrderStore(
    useShallow((state) => state.getCCAFieldDefinitionsForWo(displayWo))
  );
  const completeColumnDetails = useWorkOrderStore(
    useShallow((state) => state.getCompleteColumnDetailsForWo(displayWo))
  );
  const isNewRelationship = useWorkOrderStore(
    (state) => state.isNewRelationship
  );

  const setup = useCallback(async () => {
    setShowLoading(true);

    const sortValues = (values: ValidValue[]) =>
      [...(values || [])].sort((a, b) =>
        compareValidValuesByDisplayText(a, b, "both")
      );

    const componentValues: ValidValue[] = completeColumnDetails
      ? completeColumnDetails.component
      : ccaFieldDefinitions?.componentField?.ValidValues;
    const conditionValues: ValidValue[] = completeColumnDetails
      ? completeColumnDetails.condition
      : ccaFieldDefinitions?.conditionField?.ValidValues;
    const actionValues: ValidValue[] = completeColumnDetails
      ? completeColumnDetails.action
      : ccaFieldDefinitions?.actionField?.ValidValues;

    setComponentValues(sortValues(componentValues));
    setConditionValues(sortValues(conditionValues));
    setActionValues(sortValues(actionValues));

    const componentField: CompletionFieldDef =
      ccaFieldDefinitions?.componentField;
    const conditionField: CompletionFieldDef =
      ccaFieldDefinitions?.conditionField;
    const actionField: CompletionFieldDef = ccaFieldDefinitions?.actionField;

    !!componentField && setComponentField(componentField);
    !!conditionField && setConditionField(conditionField);
    !!actionField && setActionField(actionField);

    if (isNewRelationship) {
      const {
        componentCascadeValues,
        conditionCascadeValues,
        actionCascadeValues,
      } = await getCCACascadeValues();
      setComponentCascadeValues(componentCascadeValues);
      setConditionCascadeValues(conditionCascadeValues);
      setActionCascadeValues(actionCascadeValues);
    }

    setShowLoading(false);
  }, [
    ccaFieldDefinitions?.actionField,
    ccaFieldDefinitions?.componentField,
    ccaFieldDefinitions?.conditionField,
    completeColumnDetails,
    isNewRelationship,
  ]);

  useEffect(() => {
    if (displayWo) {
      setup();
    }
  }, [displayWo, setup]);

  useEffect(() => {
    setAnalysisCode(analysisCodeForEdit);
  }, [analysisCodeForEdit]);

  /* US 906024 - a field mapping can make the next code in the chain mandatory for the code just
     picked. Evaluated here rather than in setup() because it has to be redone every time a
     selection changes, and cleared when the modal is closed so a stale rule is not carried into
     the next code being added. */
  useEffect(() => {
    if (!isOpen) return;
    let cancelled = false;
    const workOrder = useWorkOrderStore.getState().getWorkOrderById(displayWo);
    getCCARequiredFlags({
      equipmentClass: workOrder?.EquipmentClass,
      component: analysisCode?.Component,
      condition: analysisCode?.Condition,
    }).then((flags) => {
      if (!cancelled) setCcaRequired(flags);
    });
    return () => {
      cancelled = true;
    };
  }, [isOpen, displayWo, analysisCode?.Component, analysisCode?.Condition]);

  function getFilteredValues(
    cascadeValues: CascadeValue[],
    parentValue: string | undefined,
    ancestorValue: string | undefined,
    allValidValues: ValidValue[],
    isAction: boolean = false
  ): ValidValue[] {
    if (!parentValue) return [];
    let filteredCascade = cascadeValues.filter(
      (cv) =>
        cv.ParentValue === parentValue &&
        (!isAction || cv.AncestorValue === ancestorValue)
    );
    if (filteredCascade.length > 0) {
      const codes = filteredCascade.map((cv) => cv.ChildValue);
      return allValidValues.filter((v) => codes.includes(v.Code));
    }
    // No mapping: show all valid values
    return allValidValues;
  }

  // Effect for Condition values
  useEffect(() => {
    if (isNewRelationship && analysisCode?.Component) {
      const filteredValues = getFilteredValues(
        conditionCascadeValues,
        analysisCode.Component,
        undefined,
        conditionField?.ValidValues || []
      );
      setConditionValues(
        [...filteredValues].sort((a, b) =>
          compareValidValuesByDisplayText(a, b, "both")
        )
      );

      // Only reset if user changed the component (not just opening for edit)
      if (
        !analysisCodeForEdit ||
        (analysisCode.Component && analysisCode.Component !== analysisCodeForEdit?.Component)
      ) {
        setAnalysisCode({
          ...analysisCode,
          Condition: undefined,
          Action: undefined,
        });
      }
    }
  }, [
    isNewRelationship,
    analysisCode?.Component,
    conditionCascadeValues,
    conditionField?.ValidValues,
    analysisCodeForEdit,
  ]);

  // Effect for Action values
  useEffect(() => {
    if (isNewRelationship && analysisCode?.Condition) {
      const filteredValues = getFilteredValues(
        actionCascadeValues,
        analysisCode.Condition,
        analysisCode.Component,
        actionField?.ValidValues || [],
        true
      );
      setActionValues(
        [...filteredValues].sort((a, b) =>
          compareValidValuesByDisplayText(a, b, "both")
        )
      );

      // Only reset if user changed the condition (not just opening for edit)
      if (
        !analysisCodeForEdit ||
        (analysisCode.Condition && analysisCode.Condition !== analysisCodeForEdit?.Condition)
      ) {
        setAnalysisCode({ ...analysisCode, Action: undefined });
      }
    }
  }, [
    isNewRelationship,
    analysisCode?.Component,
    analysisCode?.Condition,
    actionCascadeValues,
    actionField?.ValidValues,
    analysisCodeForEdit,
  ]);

  const onComponentSelect = (value?: string) => {
    value && setAnalysisCode({ ...analysisCode, Component: value });
  };
  const onConditionSelect = (value?: string) => {
    value && setAnalysisCode({ ...analysisCode, Condition: value });
  };
  const onActionSelect = (value?: string) => {
    value && setAnalysisCode({ ...analysisCode, Action: value });
  };

  /* US 906024 - what each field is actually held to: its own element setting OR a cascade rule.
     Never the other way round, so a mapping can add required-ness but never remove it. */
  const componentRequired = isFieldRequired({
    IsRequired: componentField?.IsRequired,
    CascadeRequired: ccaRequired.componentRequired,
  });
  const conditionRequired = isFieldRequired({
    IsRequired: conditionField?.IsRequired,
    CascadeRequired: ccaRequired.conditionRequired,
  });
  const actionRequired = isFieldRequired({
    IsRequired: actionField?.IsRequired,
    CascadeRequired: ccaRequired.actionRequired,
  });

  /* All three codes are mandatory here regardless - a partial Component/Condition/Action row is
     not a thing the server accepts - so the cascade rules cannot loosen this gate; they drive
     the required indicators above, and the completion gate in useCompleteWo /
     useGetIsCAARequiredForCompletion, which previously read only the element settings. */
  const isValidCode = () => {
    if (
      !analysisCode ||
      !analysisCode.Component ||
      !analysisCode.Condition ||
      !analysisCode.Action
    ) {
      return false;
    }
    const doesExist = analysisCodes.find((code) => {
      return (
        code.Component === analysisCode?.Component &&
        code.Condition === analysisCode?.Condition &&
        code.Action === analysisCode?.Action
      );
    });
    return !doesExist;
  };

  const resetSelectOptions = () => {
    const sortValues = (values: ValidValue[]) =>
      [...(values || [])].sort((a, b) =>
        compareValidValuesByDisplayText(a, b, "both")
      );
    if (isNewRelationship) {
      setComponentValues(sortValues(componentField?.ValidValues || []));
      setConditionValues(sortValues(conditionField?.ValidValues || []));
      setActionValues(sortValues(actionField?.ValidValues || []));
    }
  };

  /* onIonModalWillDismiss is wired to onCancel, so a backdrop tap or the Android back button
     closes the modal without going through handleCancel. The component stays mounted, so keying
     this off isOpen is what guarantees every close path leaves no field open - otherwise the
     next open could render a list straight away. */
  useEffect(() => {
    if (!isOpen) setOpenField(undefined);
  }, [isOpen]);

  const handleCancel = () => {
    setOpenField(undefined);
    onCancel();
    resetSelectOptions();
    setAnalysisCode(undefined); // Add this line to reset the analysisCode state
  };
  const handleSave = () => {
    setOpenField(undefined);
    onSave(analysisCode);
    setAnalysisCode(undefined);
    resetSelectOptions();
  };

  return (
    <IonModal isOpen={isOpen} onIonModalWillDismiss={onCancel}>
      <IonLoading
        isOpen={showLoading}
        onDidDismiss={() => {
          setShowLoading(false);
        }}
      />
      <IonHeader>
        <IonToolbar>
          <IonButtons slot="start">
            <IonButton onClick={handleCancel}>
              {translations["lbl_btn_cancel"] || "Cancel"}
            </IonButton>
          </IonButtons>
          <IonTitle>
            {analysisCodeForEdit
              ? `${translations["lbl_edit"] || "Edit"} `
              : `${translations["lbl_button_add"] || "Add"} `}
            {translations["lbl_analysis_codes"] || "Analysis Code"}
          </IonTitle>
          <IonButtons slot="end">
            <IonButton
              strong={true}
              onClick={handleSave}
              disabled={!isValidCode()}
            >
              {translations["lbl_btn_save"] || "Save"}
            </IonButton>
          </IonButtons>
        </IonToolbar>
      </IonHeader>
      <IonContent>
        <IonList lines="full">
          <CodeField
            field={componentField}
            fallbackLabel="Component"
            isRequired={componentRequired}
            values={componentValues}
            value={analysisCode?.Component}
            disabled={false}
            isOpen={openField === "component"}
            onOpenChange={(isOpen) =>
              setOpenField(isOpen ? "component" : undefined)
            }
            onSelect={onComponentSelect}
          />
          <CodeField
            field={conditionField}
            fallbackLabel="Condition"
            isRequired={conditionRequired}
            values={conditionValues}
            value={analysisCode?.Condition}
            disabled={isNewRelationship && !analysisCode?.Component}
            isOpen={openField === "condition"}
            onOpenChange={(isOpen) =>
              setOpenField(isOpen ? "condition" : undefined)
            }
            onSelect={onConditionSelect}
          />
          <CodeField
            field={actionField}
            fallbackLabel="Action"
            isRequired={actionRequired}
            values={actionValues}
            value={analysisCode?.Action}
            disabled={
              isNewRelationship &&
              (!analysisCode?.Component || !analysisCode.Condition)
            }
            isOpen={openField === "action"}
            onOpenChange={(isOpen) =>
              setOpenField(isOpen ? "action" : undefined)
            }
            onSelect={onActionSelect}
          />
        </IonList>
      </IonContent>
    </IonModal>
  );
};

export default AnalysisCodesModal;
